import React, { ChangeEvent, useState, useEffect, useContext } from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import AddAPhotoIcon from "@mui/icons-material/AddAPhoto";
import CustomInput from "../shared/custom-input/custom-input";
import CustomSelect from "../shared/custom-select/custom-select";
import CustomButton from "../shared/custom-button/custom-button";
import { classesLevel } from "../mocks/education-level";
import { SnackbarContext } from "../config/hooks/use-toast";
import { faCamera, faTimes, faUpload, faCheck } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface ClassData {
  backgroundImage: string;
  title: string;
  educationLevel: string;
}

interface ClassModalProps {
  open: boolean;
  onClose: () => void;
  modalTitle: string;
  buttonText: string;
  handleActionClick: (data: FormData) => void;
  initialData?: any;
}


const ClassModal: React.FC<ClassModalProps> = ({
  open,
  onClose,
  modalTitle,
  buttonText,
  handleActionClick,
  initialData,
}) => {
  const snackbarContext = useContext(SnackbarContext);
  const isEdit = !!initialData;

  const [formData, setFormData] = useState<ClassData>({
    backgroundImage: "",
    title: "",
    educationLevel: "",
  });
  const [backgroundImageFile, setBackgroundImageFile] = useState<File | null>(null);

  useEffect(() => {
    if (initialData) {
      setFormData({
        backgroundImage: initialData.backgroundImageUrl,
        title: initialData.title,
        educationLevel: initialData.educationLevel,
      });
    } else {
      setFormData({
        backgroundImage: "",
        title: "",
        educationLevel: "",
      });
    }
  }, [initialData, open]);

  const handleImageUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setFormData((prevData) => ({ ...prevData, backgroundImage: imageUrl }));
      setBackgroundImageFile(file);
    }
  };

  const handleSubmit = () => {
    const dataToSend = new FormData();
    const groupDetailsPayload = {
      title: formData.title,
      educationLevel: formData.educationLevel,
    };
    const serializedGroupDetails = JSON.stringify(groupDetailsPayload);

    // Keep both keys for backward compatibility across create/update endpoints.
    dataToSend.append("groupDetailsJson", serializedGroupDetails);
    dataToSend.append("groupeDetailsJson", serializedGroupDetails);
    if (backgroundImageFile) {
      dataToSend.append("backgroundImage", backgroundImageFile);
    }
    if (!backgroundImageFile && !formData.backgroundImage) {
      if (snackbarContext) {
        snackbarContext.showMessage("Erreur", "Veuillez importer une image de couverture", "error");
      }
      return;
    }
    handleActionClick(dataToSend);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      className="flex justify-end backdrop-blur-sm bg-title/20"
      closeAfterTransition
    >
      <div className={`h-full w-full max-w-[550px] bg-white shadow-[-20px_0_80px_rgba(0,0,0,0.1)] outline-none transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] flex flex-col ${open ? 'translate-x-0' : 'translate-x-full'}`}>
        
        {/* Drawer Header */}
        <div className="p-8 pb-6 flex items-center justify-between border-b border-gray-50 bg-white/50 backdrop-blur-md flex-shrink-0 z-30">
          <div>
            <h2 className="text-2xl font-black text-title uppercase tracking-tighter">{modalTitle}</h2>
            <p className="text-[9px] font-black text-primary uppercase tracking-[0.4em] mt-1">{isEdit ? "Édition du programme" : "Configuration initiale"}</p>
          </div>
          <button 
            onClick={onClose}
            className="w-12 h-12 flex items-center justify-center rounded-2xl bg-gray-50 text-text/30 hover:bg-rose-50 hover:text-rose-500 transition-all duration-300 group cursor-pointer"
          >
            <FontAwesomeIcon icon={faTimes} className="text-lg group-hover:rotate-90 transition-transform duration-500 pointer-events-none" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto no-scrollbar p-8 space-y-10 bg-white">
          
          {/* Section: Cover Illustration */}
          <section className="space-y-4">
            <div className="flex items-center justify-between">
               <h3 className="text-[11px] font-black text-title uppercase tracking-widest flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                  Illustration du cours
               </h3>
               {formData.backgroundImage && (
                 <button 
                   onClick={() => {setFormData(p => ({...p, backgroundImage: ""})); setBackgroundImageFile(null);}}
                   className="text-[9px] font-black text-rose-500 uppercase tracking-widest hover:underline"
                 >
                   Supprimer
                 </button>
               )}
            </div>

            <label className="group relative block w-full aspect-[16/9] rounded-[2.5rem] overflow-hidden cursor-pointer bg-slate-50 border-2 border-dashed border-slate-100 hover:border-primary/40 transition-all duration-700">
              {formData.backgroundImage ? (
                <>
                  <img src={formData.backgroundImage} alt="Preview" className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-title/60 backdrop-blur-[2px] opacity-0 group-hover:opacity-100 transition-all duration-500 flex items-center justify-center">
                    <div className="flex flex-col items-center text-white space-y-3 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                      <div className="w-14 h-14 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center shadow-2xl border border-white/20">
                         <FontAwesomeIcon icon={faUpload} className="text-xl" />
                      </div>
                      <span className="text-[10px] font-black uppercase tracking-[0.2em]">Remplacer l'image</span>
                    </div>
                  </div>
                </>
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-300">
                  <div className="w-20 h-20 bg-white rounded-3xl shadow-xl shadow-slate-200/50 flex items-center justify-center mb-6 group-hover:scale-110 group-hover:bg-primary group-hover:text-white transition-all duration-700">
                    <FontAwesomeIcon icon={faUpload} className="text-2xl" />
                  </div>
                  <span className="text-[11px] font-black uppercase tracking-[0.3em] text-slate-400">Importer un visuel</span>
                  <p className="text-[9px] mt-2 uppercase tracking-widest opacity-50">Dimensions idéales: 1280 x 720</p>
                </div>
              )}
              <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
            </label>
          </section>

          {/* Section: Main Details */}
          <section className="space-y-8">
             <div className="space-y-3">
                <label className="text-[11px] font-black text-title uppercase tracking-widest flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                  Désignation du programme
                </label>
                <CustomInput
                  placeholder="Ex: Mathématiques - Terminale S"
                  inputType="text"
                  CustomStyle="!h-auto !my-0"
                  inputStyle="!h-16 !bg-slate-50 !border-slate-100 focus:!bg-white focus:!border-primary/20 !rounded-2xl !px-6 !font-black !text-title !text-base transition-all shadow-sm focus:shadow-xl focus:shadow-primary/5"
                  value={formData.title}
                  name="title"
                  onChange={handleChange}
                />
             </div>

             <div className="space-y-3">
                <label className="text-[11px] font-black text-title uppercase tracking-widest flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                  Niveau d'études
                </label>
                <CustomSelect
                  customStyle="!h-auto !my-0"
                  selectStyle="!h-16 !bg-slate-50 !border-slate-100 focus:!bg-white focus:!border-primary/20 !rounded-2xl !px-6 !font-black !text-title !text-base transition-all shadow-sm focus:shadow-xl focus:shadow-primary/5"
                  options={classesLevel}
                  placeholder="Choisir un niveau..."
                  value={formData.educationLevel}
                  onChange={handleSelectChange}
                  name="educationLevel"
                />
             </div>
          </section>

          {/* Tips / Info */}
          {/* <div className="bg-primary/5 rounded-[2rem] p-6 border border-primary/10">
             <p className="text-[11px] font-bold text-primary/70 leading-relaxed uppercase tracking-wide">
                Astuce: Un titre clair et une image attrayante augmentent le taux d'engagement des élèves de plus de 40%.
             </p>
          </div> */}
        </div>

        {/* Footer Actions */}
        <div className="p-8 bg-white border-t border-gray-100 flex gap-4 z-40 flex-shrink-0">
           <button 
              onClick={onClose}
              className="px-8 h-14 rounded-2xl font-black text-[11px] uppercase tracking-widest text-text/40 hover:text-rose-500 hover:bg-rose-50 active:scale-95 transition-all duration-300 cursor-pointer"
           >
              <span className="pointer-events-none">Annuler</span>
           </button>
           <button 
              onClick={handleSubmit}
              className="flex-1 h-14 bg-primary text-white rounded-2xl font-black text-[11px] uppercase tracking-[0.3em] shadow-2xl shadow-primary/30 hover:shadow-primary/50 hover:-translate-y-1 active:scale-95 transition-all duration-300 flex items-center justify-center gap-4 group cursor-pointer"
           >
              <span className="pointer-events-none">{buttonText}</span>
              <div className="w-6 h-6 rounded-lg bg-white/20 flex items-center justify-center group-hover:rotate-90 transition-transform duration-500 pointer-events-none">
                 <FontAwesomeIcon icon={faCheck} className="text-[10px]" />
              </div>
           </button>
        </div>
      </div>
    </Modal>
  );
};

export default ClassModal;
